import React from 'react'
import AttendanceSubNav from '../components/AttendanceSubNav'
import AttendanceSettingsTipsCard from './components/AttendanceSettingsTipsCard'
import useAttendanceSettings from './hooks/useAttendanceSettings'

export default function AttendanceSettingsScreen() {
  const {
    settings,
    isSaving,
    setField,
    toggleAbsenteeSms,
    toggleAbsenteeWhatsApp,
    toggleAutoSms,
    toggleAutoWhatsApp,
    handleSave,
    handleReset,
    handleExportAbsenteeExcel,
  } = useAttendanceSettings()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Header ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
        <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
          Configuration &amp; Automation
        </p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
          Attendance Settings &amp; Policies
        </h1>
        <p className="text-xs text-on-surface-variant">
          Configure biometric automation rules, absentee notification dispatch, parent recipient policies, and duplicate swipe debounce windows.
        </p>
        </div>
        <AttendanceSubNav />
      </section>

      {/* ── 2-Column Layout: Settings Form + Tips ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Settings Configuration Card (7 Cols) */}
        <div className="flex flex-col gap-6 lg:col-span-7">
          <div className="flex flex-col gap-6 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 sm:p-8 shadow-xs">
            {/* ── Section 1: Automated Attendance & Swipes ── */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <span className="material-symbols-outlined text-base">how_to_reg</span>
                <span>Automated Attendance &amp; Reconciliations</span>
              </div>

              {/* Student Swipes Mode */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Student Swipes Sync Mode
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setField('studentSwipes', 'M')}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 text-left transition-all cursor-pointer ${
                      settings.studentSwipes === 'M'
                        ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                        : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                    }`}
                  >
                    <span className="material-symbols-outlined text-lg">edit_note</span>
                    <div>
                      <p className="text-xs font-bold text-on-surface">Manual Marking</p>
                      <p className="text-[10px] text-on-surface-variant">Staff marks attendance manually</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setField('studentSwipes', 'A')}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 text-left transition-all cursor-pointer ${
                      settings.studentSwipes === 'A'
                        ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                        : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                    }`}
                  >
                    <span className="material-symbols-outlined text-lg">auto_mode</span>
                    <div>
                      <p className="text-xs font-bold text-on-surface">Automatic (9:00 PM)</p>
                      <p className="text-[10px] text-on-surface-variant">Reconciles daily device swipe logs</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Absentee Alerts Mode */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Absentee Alerts Dispatch
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setField('absenteeAlerts', 'M')}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 text-left transition-all cursor-pointer ${
                      settings.absenteeAlerts === 'M'
                        ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                        : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                    }`}
                  >
                    <span className="material-symbols-outlined text-lg">touch_app</span>
                    <div>
                      <p className="text-xs font-bold text-on-surface">Manual Trigger</p>
                      <p className="text-[10px] text-on-surface-variant">Trigger SMS / WhatsApp on demand</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setField('absenteeAlerts', 'A')}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 text-left transition-all cursor-pointer ${
                      settings.absenteeAlerts === 'A'
                        ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                        : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                    }`}
                  >
                    <span className="material-symbols-outlined text-lg">forward_to_inbox</span>
                    <div>
                      <p className="text-xs font-bold text-on-surface">Automatic Dispatch</p>
                      <p className="text-[10px] text-on-surface-variant">Dispatches alerts after 9:00 PM</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Absentee Follow-up Days Threshold */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-3.5">
                <div>
                  <p className="text-xs font-bold text-on-surface">Absentee Follow-up Threshold</p>
                  <p className="text-[10px] text-on-surface-variant">
                    Flag students absent for more than specified consecutive days
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-on-surface-variant">&gt;</span>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={settings.misAbsenteeDays}
                    onChange={(e) => setField('misAbsenteeDays', Number(e.target.value))}
                    className="h-9 w-16 rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-center text-xs font-black text-on-surface outline-none focus:border-primary"
                  />
                  <span className="text-xs font-bold text-on-surface-variant">days</span>
                  <button
                    type="button"
                    onClick={handleExportAbsenteeExcel}
                    title="Download Defaulters Excel"
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-outline-variant/30 bg-surface-container-low text-on-surface hover:bg-surface-container hover:text-primary transition cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-base">table_view</span>
                  </button>
                </div>
              </div>
            </div>

            {/* ── Section 2: Absentee Notification Channels & Recipients ── */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <span className="material-symbols-outlined text-base">campaign</span>
                <span>Absentee Alerts Recipients</span>
              </div>

              {/* Absentee SMS Recipients */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Absentee SMS Alert To
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'father', label: 'Father / Guardian', icon: 'person' },
                    { key: 'mother', label: 'Mother', icon: 'favorite' },
                  ].map((role) => {
                    const isSelected = settings.absenteeSmsTo[role.key]
                    return (
                      <button
                        key={role.key}
                        type="button"
                        onClick={() => toggleAbsenteeSms(role.key)}
                        className={`flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/10 text-primary font-black shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">{role.icon}</span>
                        <span>{role.label}</span>
                        <span className="material-symbols-outlined text-sm">
                          {isSelected ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Absentee WhatsApp Recipients */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Absentee WhatsApp Alert To
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'father', label: 'Father / Guardian', icon: 'person' },
                    { key: 'mother', label: 'Mother', icon: 'favorite' },
                  ].map((role) => {
                    const isSelected = settings.absenteeWhatsAppTo[role.key]
                    return (
                      <button
                        key={role.key}
                        type="button"
                        onClick={() => toggleAbsenteeWhatsApp(role.key)}
                        className={`flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 font-black shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">chat</span>
                        <span>{role.label}</span>
                        <span className="material-symbols-outlined text-sm">
                          {isSelected ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* ── Section 3: Real-Time Gate Swipes (Auto SMS & WhatsApp) ── */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <span className="material-symbols-outlined text-base">sync_alt</span>
                <span>Real-Time Gate Swipe Notifications</span>
              </div>

              {/* Gate Direction Triggers */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Real-Time Swipe Event Triggers
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setField('autoSmsIn', !settings.autoSmsIn)}
                    className={`flex items-center justify-between rounded-2xl border p-3 text-xs font-bold transition-all cursor-pointer ${
                      settings.autoSmsIn
                        ? 'border-primary bg-primary/10 text-primary font-black shadow-2xs'
                        : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">login</span>
                      <span>In (Arrival Scan)</span>
                    </div>
                    <span className="material-symbols-outlined text-base">
                      {settings.autoSmsIn ? 'check_box' : 'check_box_outline_blank'}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setField('autoSmsOut', !settings.autoSmsOut)}
                    className={`flex items-center justify-between rounded-2xl border p-3 text-xs font-bold transition-all cursor-pointer ${
                      settings.autoSmsOut
                        ? 'border-primary bg-primary/10 text-primary font-black shadow-2xs'
                        : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">logout</span>
                      <span>Out (Departure Scan)</span>
                    </div>
                    <span className="material-symbols-outlined text-base">
                      {settings.autoSmsOut ? 'check_box' : 'check_box_outline_blank'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Auto SMS Recipients */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Gate Swipe Auto SMS To
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'father', label: 'Father / Guardian', icon: 'person' },
                    { key: 'mother', label: 'Mother', icon: 'favorite' },
                  ].map((role) => {
                    const isSelected = settings.autoSmsTo[role.key]
                    return (
                      <button
                        key={role.key}
                        type="button"
                        onClick={() => toggleAutoSms(role.key)}
                        className={`flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/10 text-primary font-black shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">{role.icon}</span>
                        <span>{role.label}</span>
                        <span className="material-symbols-outlined text-sm">
                          {isSelected ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Auto WhatsApp Recipients */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Gate Swipe Auto WhatsApp To
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'father', label: 'Father / Guardian', icon: 'person' },
                    { key: 'mother', label: 'Mother', icon: 'favorite' },
                  ].map((role) => {
                    const isSelected = settings.autoWhatsAppTo[role.key]
                    return (
                      <button
                        key={role.key}
                        type="button"
                        onClick={() => toggleAutoWhatsApp(role.key)}
                        className={`flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 font-black shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">chat</span>
                        <span>{role.label}</span>
                        <span className="material-symbols-outlined text-sm">
                          {isSelected ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Minimum Debounce Gap */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-3.5">
                <div>
                  <p className="text-xs font-bold text-on-surface">Minimum Swipe Debounce Gap</p>
                  <p className="text-[10px] text-on-surface-variant">
                    Ignores accidental duplicate card scans within the given duration
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={settings.autoSmsMinGap}
                    onChange={(e) => setField('autoSmsMinGap', Number(e.target.value))}
                    className="h-9 w-20 rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-center text-xs font-black text-on-surface outline-none focus:border-primary"
                  />
                  <span className="text-xs font-bold text-on-surface-variant">minutes</span>
                </div>
              </div>
            </div>

            {/* ── Footer Actions ── */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition cursor-pointer"
              >
                <span className="material-symbols-outlined text-base">restart_alt</span>
                <span>Reset Defaults</span>
              </button>

              <button
                type="button"
                disabled={isSaving}
                onClick={handleSave}
                className="flex items-center gap-2 rounded-xl bg-primary px-6 py-2 text-xs font-extrabold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-base">
                  {isSaving ? 'progress_activity' : 'save'}
                </span>
                <span>{isSaving ? 'Saving...' : 'Save Settings'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Tips & Best Practices (5 Cols) */}
        <div className="lg:col-span-5">
          <AttendanceSettingsTipsCard />
        </div>
      </div>
    </div>
  )
}
